import { getShareUrl, shouldUseNativeShare } from '../lib/shareLink'

const TOAST_MS = 2600
const GAP = 8 // px between the button and the message
const EDGE = 8 // px kept clear of the viewport edge
const COPIED_FALLBACK = 'Link copied to clipboard'
const FAILED_FALLBACK = 'Couldn’t copy the link. Copy it from the address bar instead.'

/**
 * Share buttons (`[ck-share]`), all of which share the current page.
 *
 * - touch-first devices with the Web Share API → the native share sheet
 * - everything else → the link is copied and a small "Link copied to clipboard" toast confirms it
 *
 * The toast is a single Webflow element (`[ck-share-toast]`, in the Navbar component). Its text
 * is the confirmation copy, editable in the Designer; `ck-share-error` holds the failure copy.
 * CSS reveals it while `data-ck-visible` is set.
 *
 * Placement follows the attribute's value:
 * - `ck-share-toast="anchor"` → right next to the button that was pressed, so it cannot be missed
 * - anything else             → wherever the Webflow class puts it (bottom centre of the viewport)
 */
export default function Share() {
  const buttons = document.querySelectorAll<HTMLElement>('[ck-share]')
  if (!buttons.length) return

  const toast = document.querySelector<HTMLElement>('[ck-share-toast]')
  const copiedText = toast?.textContent?.trim() || COPIED_FALLBACK
  const failedText = toast?.getAttribute('ck-share-error') || FAILED_FALLBACK
  // A live region only announces changes, so it starts empty and is filled on demand.
  if (toast) toast.textContent = ''

  const anchored = toast?.getAttribute('ck-share-toast') === 'anchor'
  let hideTimer: number | undefined
  let frame: number | undefined

  const hide = () => {
    toast?.removeAttribute('data-ck-visible')
    if (frame) cancelAnimationFrame(frame)
    frame = undefined
  }

  const announce = (message: string, button: HTMLElement) => {
    if (!toast) return
    window.clearTimeout(hideTimer)
    if (frame) cancelAnimationFrame(frame)
    toast.textContent = message
    if (anchored) {
      // Stay glued to the button for the couple of seconds the message is up, whether the page
      // scrolls or the button itself is still easing into place from an entrance animation.
      const follow = () => {
        placeBeside(toast, button)
        frame = requestAnimationFrame(follow)
      }
      follow()
    }
    toast.setAttribute('data-ck-visible', '')
    hideTimer = window.setTimeout(hide, TOAST_MS)
  }

  const share = async (button: HTMLElement) => {
    const url = getShareUrl(location)

    const native = shouldUseNativeShare({
      canShare: typeof navigator.share === 'function',
      coarsePointer: matchMedia('(pointer: coarse)').matches,
    })
    if (native) {
      try {
        await navigator.share({ title: document.title, url })
        return
      } catch (error) {
        // Closing the sheet is a choice, not a failure
        if ((error as DOMException)?.name === 'AbortError') return
      }
    }

    announce((await copy(url)) ? copiedText : failedText, button)
  }

  buttons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault()
      share(button)
    })
    // Webflow renders these as links with role="button"; give them native button keys.
    button.addEventListener('keydown', (event) => {
      if (event.key === ' ' && button.tagName !== 'BUTTON') {
        event.preventDefault()
        button.click()
      }
    })
  })
}

/**
 * Pins the (position: fixed) toast just below the button, centred on it. Flips above when there
 * is no room underneath, and never lets it run off the side of the viewport.
 */
function placeBeside(toast: HTMLElement, button: HTMLElement) {
  const target = button.getBoundingClientRect()
  const width = toast.offsetWidth
  const height = toast.offsetHeight

  const fitsBelow = target.bottom + GAP + height <= innerHeight - EDGE
  const top = fitsBelow ? target.bottom + GAP : target.top - GAP - height
  const centred = target.left + target.width / 2 - width / 2
  const left = Math.min(Math.max(centred, EDGE), Math.max(EDGE, innerWidth - width - EDGE))

  Object.assign(toast.style, {
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
    right: 'auto',
    bottom: 'auto',
    margin: '0',
  })
}

async function copy(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // permission denied or unavailable: try the legacy path below
  }

  const field = document.createElement('textarea')
  field.value = text
  field.setAttribute('readonly', '')
  field.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;'
  document.body.appendChild(field)
  field.select()
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }
  field.remove()
  return ok
}
